import { createContext, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';

const NotificationContext = createContext(null);

const config = {
  position: 'top-right',
  autoClose: 2500,
  pauseOnHover: true,
  theme: 'light',
};

/**
 * Thin wrapper over react-toastify so components depend on one app-level API rather than the
 * library directly. Also knows how to render a backend ApiResponse validation error.
 */
export function NotificationProvider({ children }) {
  const value = useMemo(
    () => ({
      info: (m) => toast.info(m, config),
      success: (m) => toast.success(m, config),
      warning: (m) => toast.warn(m, config),
      error: (m) => toast.error(typeof m === 'string' ? m : m?.message || 'Something went wrong', config),
      apiError: (err) => {
        if (err?.errors && typeof err.errors === 'object') {
          Object.values(err.errors).forEach((msg) => toast.error(msg, config));
          return;
        }
        toast.error(err?.message || 'Request failed', config);
      },
    }),
    []
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within <NotificationProvider>');
  return ctx;
}

export default NotificationContext;
